import { useState, useEffect, useCallback } from 'react';
import { safeGetJSON } from '@/lib/safeStorage';

export interface Fragment {
  id: string;
  roomId: string;
  category: 'free' | 'premium' | 'horror';
  name: string;
  collectedAt: number;
}

export interface Challenge {
  id: string;
  title: string;
  description: string;
  requirement: number;
  type: 'rooms_completed' | 'time_spent' | 'specific_rooms' | 'horror_completed' | 'nothing_room_time' | 'time_visit';
  targetRooms?: string[];
  reward: 'horror_unlock' | 'boss_hint' | 'fragment' | 'special_room';
  completed: boolean;
}

export interface ProgressState {
  completedRooms: string[];
  fragments: Fragment[];
  unlockedHorror: boolean;
  completedChallenges: string[];
  bossRoomsUnlocked: {
    free: boolean;
    premium: boolean;
    horror: boolean;
  };
  totalTimeSpent: number;
  nothingRoomTime: number;
  visitedAt317AM: boolean;
  horrorSessionCount: number;
}

const FRAGMENTS_TO_UNLOCK_BOSS = 5;
const HORROR_FRAGMENTS_TO_UNLOCK_BOSS = 3;
const PREMIUM_FRAGMENTS_TO_UNLOCK_BOSS = 4;

const defaultState: ProgressState = {
  completedRooms: [],
  fragments: [],
  unlockedHorror: false,
  completedChallenges: [],
  bossRoomsUnlocked: {
    free: false,
    premium: false,
    horror: false,
  },
  totalTimeSpent: 0,
  nothingRoomTime: 0,
  visitedAt317AM: false,
  horrorSessionCount: 0,
};

const challenges: Challenge[] = [
  {
    id: 'first-truth',
    title: 'Face the Truth',
    description: 'Complete 3 rooms from any category',
    requirement: 3,
    type: 'rooms_completed',
    reward: 'horror_unlock',
    completed: false,
  },
  {
    id: 'time-keeper',
    title: 'Time Keeper',
    description: 'Spend 10 minutes in the rooms',
    requirement: 600,
    type: 'time_spent',
    reward: 'fragment',
    completed: false,
  },
  {
    id: 'identity-seeker',
    title: 'Identity Seeker',
    description: 'Complete all identity rooms',
    requirement: 3,
    type: 'specific_rooms',
    targetRooms: ['one-honest-sentence', 'blurred-self', 'thought-loop'],
    reward: 'boss_hint',
    completed: false,
  },
  {
    id: 'night-walker',
    title: 'Night Walker',
    description: 'Complete 5 rooms to unlock the darkness',
    requirement: 5,
    type: 'rooms_completed',
    reward: 'horror_unlock',
    completed: false,
  },
  {
    id: 'deep-diver',
    title: 'Deep Diver',
    description: 'Complete 7 different rooms',
    requirement: 7,
    type: 'rooms_completed',
    reward: 'fragment',
    completed: false,
  },
  // NEW CHALLENGES
  {
    id: 'nothing-master',
    title: 'Master of Nothing',
    description: 'Stay in Nothing Happens for 2 minutes',
    requirement: 120,
    type: 'nothing_room_time',
    reward: 'fragment',
    completed: false,
  },
  {
    id: 'witching-hour',
    title: 'The Witching Hour',
    description: 'Visit a room at exactly 3:17 AM',
    requirement: 1,
    type: 'time_visit',
    targetRooms: ['317am-room'],
    reward: 'special_room',
    completed: false,
  },
  {
    id: 'horror-survivor',
    title: 'Horror Survivor',
    description: 'Complete 5 horror rooms in one session',
    requirement: 5,
    type: 'horror_completed',
    reward: 'fragment',
    completed: false,
  },
  {
    id: 'truth-seeker',
    title: 'Truth Seeker',
    description: 'Complete all truth category rooms',
    requirement: 5,
    type: 'specific_rooms',
    targetRooms: ['delay-button', 'reality-receipt', 'default-choice', 'expect-vs-happens', 'not-special'],
    reward: 'fragment',
    completed: false,
  },
  {
    id: 'time-traveler',
    title: 'Time Traveler',
    description: 'Complete all time category rooms',
    requirement: 3,
    type: 'specific_rooms',
    targetRooms: ['life-in-weeks', 'this-moment', 'day-in-pixels'],
    reward: 'fragment',
    completed: false,
  },
  {
    id: 'explorer',
    title: 'The Explorer',
    description: 'Complete 15 different rooms',
    requirement: 15,
    type: 'rooms_completed',
    reward: 'fragment',
    completed: false,
  },
  {
    id: 'darkness-embracer',
    title: 'Embrace the Dark',
    description: 'Complete all horror rooms',
    requirement: 12,
    type: 'horror_completed',
    reward: 'boss_hint',
    completed: false,
  },
];

const fragmentNames = {
  free: ['Shard of Clarity', 'Echo of Time', 'Whisper of Truth', 'Mirror Fragment', 'Dust of Memory', 'Thread of Fate', 'Tear of Silence', 'Breath of Now'],
  premium: ['Golden Truth', 'Silver Reflection', 'Crystal of Depth', 'Ember of Change', 'Star of Identity', 'Pearl of Wisdom'],
  horror: ['Shadow Essence', 'Fear Fragment', 'Dark Whisper', 'Void Shard', 'Nightmare Core', 'Phantom Tear'],
};

export const useProgression = () => {
  const [state, setState] = useState<ProgressState>(() => {
    return safeGetJSON<ProgressState>('human-rooms-progress', defaultState);
  });

  useEffect(() => {
    localStorage.setItem('human-rooms-progress', JSON.stringify(state));
  }, [state]);

  const completeRoom = useCallback((roomId: string, category: 'free' | 'premium' | 'horror') => {
    setState(prev => {
      if (prev.completedRooms.includes(roomId)) {
        return prev;
      }

      const newCompletedRooms = [...prev.completedRooms, roomId];
      
      // Award fragment
      const fragmentId = `${roomId}-fragment`;
      const existingFragment = prev.fragments.find(f => f.id === fragmentId);
      
      let newFragments = prev.fragments;
      if (!existingFragment) {
        const names = fragmentNames[category];
        const fragmentName = names[prev.fragments.filter(f => f.category === category).length % names.length];
        
        newFragments = [...prev.fragments, {
          id: fragmentId,
          roomId,
          category,
          name: fragmentName,
          collectedAt: Date.now(),
        }];
      }

      // Check boss room unlocks
      const freeFragments = newFragments.filter(f => f.category === 'free').length;
      const premiumFragments = newFragments.filter(f => f.category === 'premium').length;
      const horrorFragments = newFragments.filter(f => f.category === 'horror').length;

      const newBossRoomsUnlocked = {
        free: freeFragments >= FRAGMENTS_TO_UNLOCK_BOSS,
        premium: premiumFragments >= PREMIUM_FRAGMENTS_TO_UNLOCK_BOSS,
        horror: horrorFragments >= HORROR_FRAGMENTS_TO_UNLOCK_BOSS,
      };

      // Track horror session
      const newHorrorSessionCount = category === 'horror' ? prev.horrorSessionCount + 1 : prev.horrorSessionCount;

      // Check challenges
      let newCompletedChallenges = [...prev.completedChallenges];
      let unlockedHorror = prev.unlockedHorror;

      const horrorRoomsCompleted = newCompletedRooms.filter(id => 
        ['theyre-watching', 'the-last-visitor', 'dont-look-away', 'the-whisper', 'you-forgot-something', 
         'the-mirror-lies', 'something-follows', 'the-countdown', 'eyes-in-the-dark', 'the-voice',
         'it-knows-your-name', 'the-door'].includes(id)
      ).length;

      challenges.forEach(challenge => {
        if (newCompletedChallenges.includes(challenge.id)) return;

        let completed = false;
        if (challenge.type === 'rooms_completed') {
          completed = newCompletedRooms.length >= challenge.requirement;
        } else if (challenge.type === 'specific_rooms' && challenge.targetRooms) {
          completed = challenge.targetRooms.every(r => newCompletedRooms.includes(r));
        } else if (challenge.type === 'horror_completed') {
          completed = horrorRoomsCompleted >= challenge.requirement || newHorrorSessionCount >= challenge.requirement;
        }

        if (completed) {
          newCompletedChallenges.push(challenge.id);
          if (challenge.reward === 'horror_unlock') {
            unlockedHorror = true;
          }
        }
      });

      return {
        ...prev,
        completedRooms: newCompletedRooms,
        fragments: newFragments,
        bossRoomsUnlocked: newBossRoomsUnlocked,
        completedChallenges: newCompletedChallenges,
        unlockedHorror,
        horrorSessionCount: newHorrorSessionCount,
      };
    });
  }, []);

  const addTime = useCallback((seconds: number) => {
    setState(prev => {
      const newTime = prev.totalTimeSpent + seconds;
      
      // Check time-based challenges
      let newCompletedChallenges = [...prev.completedChallenges];
      let newFragments = prev.fragments;

      challenges.forEach(challenge => {
        if (challenge.type === 'time_spent' && !newCompletedChallenges.includes(challenge.id)) {
          if (newTime >= challenge.requirement) {
            newCompletedChallenges.push(challenge.id);
            if (challenge.reward === 'fragment') {
              newFragments = [...newFragments, {
                id: 'time-reward-fragment',
                roomId: 'time-challenge',
                category: 'free',
                name: 'Hourglass Shard',
                collectedAt: Date.now(),
              }];
            }
          }
        }
      });

      return {
        ...prev,
        totalTimeSpent: newTime,
        completedChallenges: newCompletedChallenges,
        fragments: newFragments,
      };
    });
  }, []);

  const addNothingRoomTime = useCallback((seconds: number) => {
    setState(prev => {
      const newTime = prev.nothingRoomTime + seconds;
      let newCompletedChallenges = [...prev.completedChallenges];
      let newFragments = prev.fragments;

      const nothingChallenge = challenges.find(c => c.id === 'nothing-master');
      if (nothingChallenge && !newCompletedChallenges.includes('nothing-master') && newTime >= nothingChallenge.requirement) {
        newCompletedChallenges.push('nothing-master');
        newFragments = [...newFragments, {
          id: 'nothing-master-fragment',
          roomId: 'nothing-happens',
          category: 'free',
          name: 'Void Crystal',
          collectedAt: Date.now(),
        }];
      }

      return {
        ...prev,
        nothingRoomTime: newTime,
        completedChallenges: newCompletedChallenges,
        fragments: newFragments,
      };
    });
  }, []);

  const recordVisitAt317AM = useCallback(() => {
    setState(prev => {
      if (prev.visitedAt317AM) return prev;
      
      let newCompletedChallenges = [...prev.completedChallenges];
      if (!newCompletedChallenges.includes('witching-hour')) {
        newCompletedChallenges.push('witching-hour');
      }

      return {
        ...prev,
        visitedAt317AM: true,
        completedChallenges: newCompletedChallenges,
      };
    });
  }, []);

  const getChallenges = useCallback(() => {
    return challenges.map(c => ({
      ...c,
      completed: state.completedChallenges.includes(c.id),
    }));
  }, [state.completedChallenges]);

  const getFragmentCount = useCallback((category: 'free' | 'premium' | 'horror') => {
    return state.fragments.filter(f => f.category === category).length;
  }, [state.fragments]);

  const isRoomCompleted = useCallback((roomId: string) => {
    return state.completedRooms.includes(roomId);
  }, [state.completedRooms]);

  return {
    state,
    completeRoom,
    addTime,
    addNothingRoomTime,
    recordVisitAt317AM,
    getChallenges,
    getFragmentCount,
    isRoomCompleted,
    fragmentsNeeded: {
      free: FRAGMENTS_TO_UNLOCK_BOSS,
      premium: PREMIUM_FRAGMENTS_TO_UNLOCK_BOSS,
      horror: HORROR_FRAGMENTS_TO_UNLOCK_BOSS,
    },
  };
};

export { challenges };
